import React, { useState, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Bell,
  Mail,
  MessageSquare,
  Zap,
  Shield,
  Key,
  SlidersHorizontal,
  Plus,
  Trash2,
  GripVertical
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export default function SettingsPage() {
  const { user, setUser } = useContext(AuthContext);
  const [notifications, setNotifications] = useState({
    email_new_lead: true,
    sms_new_lead: false,
    email_daily_summary: true,
    push_notifications: true
  });
  const [twilioConfig, setTwilioConfig] = useState({
    account_sid: "",
    auth_token: "",
    phone_number: ""
  });
  const [saving, setSaving] = useState(false);

  // Check if user is owner
  const isOwner = user?.role === "owner";

  const handleSaveNotifications = async () => {
    setSaving(true);
    // In a real app, this would save to the backend
    setTimeout(() => {
      setSaving(false);
      toast.success("Notification preferences saved");
    }, 500);
  };

  const handleSaveTwilio = async () => {
    setSaving(true);
    // In a real app, this would save Twilio config to the backend
    setTimeout(() => {
      setSaving(false);
      toast.success("Twilio settings saved");
    }, 500);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-slate-600 mt-1">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          {isOwner && (
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="integrations" data-testid="tab-integrations">
              <Zap className="w-4 h-4 mr-2" />
              Integrations
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="custom-fields" data-testid="tab-custom-fields">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Custom Fields
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="automation" data-testid="tab-automation">
              <Zap className="w-4 h-4 mr-2" />
              Automation
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="calendar-sync" data-testid="tab-calendar-sync">
              <Key className="w-4 h-4 mr-2" />
              Calendar Sync
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-2xl">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{user?.name}</h3>
                  <p className="text-slate-500">{user?.email}</p>
                  <p className="text-sm text-slate-400 mt-1">
                    User ID: {user?.user_id}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="form-field">
                  <Label>Full Name</Label>
                  <Input value={user?.name || ""} disabled className="bg-slate-50" />
                </div>
                <div className="form-field">
                  <Label>Email</Label>
                  <Input value={user?.email || ""} disabled className="bg-slate-50" />
                </div>
              </div>

              <p className="text-sm text-slate-500">
                <Shield className="w-4 h-4 inline mr-1" />
                Your account is secured with Google authentication
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab - Owner Only */}
        {isOwner && (
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Email on new lead</p>
                      <p className="text-sm text-slate-500">Get notified via email when a new lead comes in</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifications.email_new_lead}
                    onCheckedChange={(v) => setNotifications({ ...notifications, email_new_lead: v })}
                    data-testid="notify-email-new-lead"
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">SMS on new lead</p>
                      <p className="text-sm text-slate-500">Get SMS alerts for new leads (requires Twilio)</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifications.sms_new_lead}
                    onCheckedChange={(v) => setNotifications({ ...notifications, sms_new_lead: v })}
                    data-testid="notify-sms-new-lead"
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Daily summary email</p>
                      <p className="text-sm text-slate-500">Receive a daily summary of your lead activity</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifications.email_daily_summary}
                    onCheckedChange={(v) => setNotifications({ ...notifications, email_daily_summary: v })}
                    data-testid="notify-daily-summary"
                  />
                </div>
              </div>

              <Button onClick={handleSaveNotifications} disabled={saving} data-testid="save-notifications">
                {saving ? "Saving..." : "Save Preferences"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Integrations Tab - Owner Only */}
        {isOwner && (
        <TabsContent value="integrations">
          <div className="space-y-6">
            {/* Twilio */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <CardTitle>Twilio SMS</CardTitle>
                    <CardDescription>Send SMS alerts and follow-ups to leads</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="form-field">
                  <Label>Account SID</Label>
                  <Input
                    value={twilioConfig.account_sid}
                    onChange={(e) => setTwilioConfig({ ...twilioConfig, account_sid: e.target.value })}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    data-testid="twilio-sid"
                  />
                </div>
                <div className="form-field">
                  <Label>Auth Token</Label>
                  <Input
                    type="password"
                    value={twilioConfig.auth_token}
                    onChange={(e) => setTwilioConfig({ ...twilioConfig, auth_token: e.target.value })}
                    placeholder="Your auth token"
                    data-testid="twilio-token"
                  />
                </div>
                <div className="form-field">
                  <Label>Phone Number</Label>
                  <Input
                    value={twilioConfig.phone_number}
                    onChange={(e) => setTwilioConfig({ ...twilioConfig, phone_number: e.target.value })}
                    placeholder="+15551234567"
                    data-testid="twilio-phone"
                  />
                </div>
                <Button onClick={handleSaveTwilio} disabled={saving} data-testid="save-twilio">
                  {saving ? "Saving..." : "Save Twilio Settings"}
                </Button>
              </CardContent>
            </Card>

            {/* Webhook Info */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Key className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle>Webhook Integration</CardTitle>
                    <CardDescription>Receive leads from external sources</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  Use webhooks to automatically capture leads from Google Ads, Facebook Ads, Zapier, and other sources.
                </p>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Webhook URL Format:</p>
                  <code className="text-sm text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                    {process.env.REACT_APP_BACKEND_URL}/api/webhook/lead/&#123;form_id&#125;
                  </code>
                  <p className="text-sm text-slate-500 mt-3">
                    Create a Lead Form to get your unique webhook URL, then use it in your advertising platforms to automatically capture leads.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* AI Features */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle>AI Features</CardTitle>
                    <CardDescription>Powered by Emergent LLM</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">AI Features Active</p>
                  <p className="text-green-700 text-sm mt-1">
                    Your account includes AI-powered lead scoring and response drafting capabilities.
                  </p>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                    AI Lead Scoring - Automatically prioritize your best leads
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                    AI Response Drafts - Generate personalized follow-up messages
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}
        {isOwner && (
        <TabsContent value="custom-fields">
          <CustomFieldsManager />
        </TabsContent>
        )}
        {isOwner && (
        <TabsContent value="automation">
          <AutomationSettings />
        </TabsContent>
        )}
        {isOwner && (
        <TabsContent value="calendar-sync">
          <GoogleCalendarSync />
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}


const ENTITY_TYPES = [
  { value: "lead", label: "Leads" },
  { value: "estimate", label: "Estimates" },
  { value: "project", label: "Projects" },
];

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "dropdown", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "multi_select", label: "Multi-Select" },
  { value: "file", label: "File Upload" },
];

function CustomFieldsManager() {
  const [fields, setFields] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [entityFilter, setEntityFilter] = React.useState("all");
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [newField, setNewField] = React.useState({
    entity_type: "lead", field_name: "", field_type: "text", options: [], required: false
  });
  const [optionInput, setOptionInput] = React.useState("");

  React.useEffect(() => { fetchFields(); }, []);

  const fetchFields = async () => {
    try {
      const res = await axios.get(`${API}/custom-fields`);
      setFields(res.data || []);
    } catch (err) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newField.field_name.trim()) { toast.error("Field name is required"); return; }
    if ((newField.field_type === "dropdown" || newField.field_type === "multi_select") && newField.options.length === 0) {
      toast.error("Add at least one option"); return;
    }
    try {
      await axios.post(`${API}/custom-fields`, newField);
      toast.success("Custom field created");
      setShowAddModal(false);
      setNewField({ entity_type: "lead", field_name: "", field_type: "text", options: [], required: false });
      setOptionInput("");
      fetchFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create field");
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm("Delete this custom field? Existing data will be preserved but hidden.")) return;
    try {
      await axios.delete(`${API}/custom-fields/${fieldId}`);
      toast.success("Field deleted");
      fetchFields();
    } catch (err) {
      toast.error("Failed to delete field");
    }
  };

  const addOption = () => {
    if (!optionInput.trim()) return;
    setNewField({ ...newField, options: [...newField.options, optionInput.trim()] });
    setOptionInput("");
  };

  const filtered = entityFilter === "all" ? fields : fields.filter(f => f.entity_type === entityFilter);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Custom Fields</CardTitle>
              <CardDescription className="mt-1">Add custom data fields to Leads, Estimates, and Projects</CardDescription>
            </div>
            <Button onClick={() => setShowAddModal(true)} data-testid="add-custom-field-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Field
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter */}
          <div className="flex gap-2 mb-4">
            <Button variant={entityFilter === "all" ? "secondary" : "ghost"} size="sm" onClick={() => setEntityFilter("all")}>All</Button>
            {ENTITY_TYPES.map(e => (
              <Button key={e.value} variant={entityFilter === e.value ? "secondary" : "ghost"} size="sm" onClick={() => setEntityFilter(e.value)}>
                {e.label}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <SlidersHorizontal className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No custom fields yet</p>
              <p className="text-sm text-slate-400">Add fields to capture additional data on your records</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(field => (
                <div key={field.field_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`field-${field.field_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{field.field_name}</span>
                      <Badge variant="outline" className="text-xs capitalize">{field.entity_type}</Badge>
                      <Badge className="text-xs bg-slate-200 text-slate-600">{field.field_type.replace("_", " ")}</Badge>
                      {field.required && <Badge className="text-xs bg-red-100 text-red-600">Required</Badge>}
                    </div>
                    {field.options && field.options.length > 0 && (
                      <p className="text-xs text-slate-400 mt-1 truncate">Options: {field.options.join(", ")}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 h-8 w-8" onClick={() => handleDelete(field.field_id)} data-testid={`delete-field-${field.field_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Field Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Entity Type</Label>
              <Select value={newField.entity_type} onValueChange={(v) => setNewField({ ...newField, entity_type: v })}>
                <SelectTrigger data-testid="cf-entity-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Field Name</Label>
              <Input value={newField.field_name} onChange={(e) => setNewField({ ...newField, field_name: e.target.value })} placeholder="e.g. Referral Source" data-testid="cf-field-name" />
            </div>
            <div>
              <Label>Field Type</Label>
              <Select value={newField.field_type} onValueChange={(v) => setNewField({ ...newField, field_type: v, options: [] })}>
                <SelectTrigger data-testid="cf-field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(newField.field_type === "dropdown" || newField.field_type === "multi_select") && (
              <div>
                <Label>Options</Label>
                <div className="flex gap-2 mb-2">
                  <Input value={optionInput} onChange={(e) => setOptionInput(e.target.value)} placeholder="Add option..." onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }} data-testid="cf-option-input" />
                  <Button type="button" variant="outline" size="sm" onClick={addOption}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {newField.options.map((opt, i) => (
                    <Badge key={i} variant="secondary" className="text-xs cursor-pointer" onClick={() => setNewField({ ...newField, options: newField.options.filter((_, idx) => idx !== i) })}>
                      {opt} x
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} className="w-4 h-4 rounded" data-testid="cf-required" />
              <Label>Required field</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="cf-create-btn">Create Field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AutomationSettings() {
  const [settings, setSettings] = React.useState({
    follow_ups_enabled: true,
    google_review_url: "",
    review_requests_enabled: true,
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/settings/org`);
        if (res.data) setSettings(prev => ({ ...prev, ...res.data }));
      } catch (err) {
        // Error handled silently — non-critical operation
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/org`, settings);
      toast.success("Automation settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Follow-up Sequences */}
      <Card>
        <CardHeader>
          <CardTitle>Automated Follow-up Sequences</CardTitle>
          <CardDescription>Automatically follow up with new leads that haven't responded</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Enable Auto Follow-ups</p>
              <p className="text-xs text-slate-500">Send SMS + email at Day 1, 3, and 7 for new leads with no response</p>
            </div>
            <Switch
              checked={settings.follow_ups_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, follow_ups_enabled: v })}
              data-testid="toggle-followups"
            />
          </div>
          <Separator />
          <div className="space-y-2 text-sm text-slate-600">
            <p className="font-medium text-slate-800">Follow-up Schedule:</p>
            <div className="flex items-center gap-3 p-2 bg-blue-50 rounded">
              <span className="font-bold text-blue-600 w-16">Day 1</span>
              <span>Thank you message + invitation to chat</span>
            </div>
            <div className="flex items-center gap-3 p-2 bg-yellow-50 rounded">
              <span className="font-bold text-yellow-600 w-16">Day 3</span>
              <span>Friendly check-in + free estimate offer</span>
            </div>
            <div className="flex items-center gap-3 p-2 bg-orange-50 rounded">
              <span className="font-bold text-orange-600 w-16">Day 7</span>
              <span>Final follow-up — no pressure, door is open</span>
            </div>
          </div>
          <p className="text-xs text-slate-400">Messages are personalized with the lead's name. Follow-ups stop automatically when a lead responds.</p>
        </CardContent>
      </Card>

      {/* Google Review Requests */}
      <Card>
        <CardHeader>
          <CardTitle>Google Review Requests</CardTitle>
          <CardDescription>Automatically ask for reviews when a project is completed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Enable Review Requests</p>
              <p className="text-xs text-slate-500">Send SMS + email with Google Review link when project marked as completed</p>
            </div>
            <Switch
              checked={settings.review_requests_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, review_requests_enabled: v })}
              data-testid="toggle-reviews"
            />
          </div>
          <div>
            <Label>Google Business Review URL</Label>
            <Input
              value={settings.google_review_url || ""}
              onChange={(e) => setSettings({ ...settings, google_review_url: e.target.value })}
              placeholder="https://g.page/r/YOUR_BUSINESS/review"
              data-testid="google-review-url"
            />
            <p className="text-xs text-slate-400 mt-1">Find this in Google Business Profile &gt; Get more reviews &gt; Share review link</p>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} data-testid="save-automation-settings">
        {saving ? "Saving..." : "Save Automation Settings"}
      </Button>
    </div>
  );
}


function GoogleCalendarSync() {
  const [status, setStatus] = React.useState({ connected: false, connected_at: null });
  const [loading, setLoading] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/calendar/google/status`);
        setStatus(res.data);
      } catch (err) {
        // Error handled silently — non-critical operation
      } finally {
        setLoading(false);
      }
    };
    load();

    // Check URL params for callback result
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "success") {
      toast.success("Google Calendar connected!");
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else if (params.get("gcal") === "error") {
      toast.error(`Google Calendar connection failed: ${params.get("msg") || "unknown error"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/calendar/google/connect`);
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start connection");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try {
      await axios.post(`${API}/calendar/google/disconnect`);
      setStatus({ connected: false });
      toast.success("Disconnected");
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  const handleSync = async (direction) => {
    setSyncing(true);
    try {
      const endpoint = direction === "to" ? "sync-to-google" : "sync-from-google";
      const res = await axios.post(`${API}/calendar/google/${endpoint}`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Google Calendar Sync</CardTitle>
          <CardDescription>Connect your Google Calendar for two-way sync with your CRM schedule</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.connected ? (
            <>
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-green-700">Google Calendar Connected</p>
                  {status.connected_at && <p className="text-xs text-green-500">Since {new Date(status.connected_at).toLocaleDateString()}</p>}
                </div>
                <Button variant="outline" size="sm" className="text-red-600 border-red-200" onClick={handleDisconnect}>
                  Disconnect
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => handleSync("to")} disabled={syncing} data-testid="sync-to-google">
                  {syncing ? "Syncing..." : "Push CRM → Google"}
                </Button>
                <Button variant="outline" onClick={() => handleSync("from")} disabled={syncing} data-testid="sync-from-google">
                  {syncing ? "Syncing..." : "Pull Google → CRM"}
                </Button>
              </div>
              <p className="text-xs text-slate-400">Push sends your CRM schedule to Google Calendar. Pull imports Google Calendar events into your CRM.</p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 bg-slate-50 border rounded-lg">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-700">Google Calendar Not Connected</p>
                  <p className="text-xs text-slate-500">Connect to sync your CRM schedule with Google Calendar</p>
                </div>
              </div>
              <Button onClick={handleConnect} className="w-full" data-testid="connect-google-cal">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Connect Google Calendar
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
